import { buildSearchIntent, displaySearchTerm, getVehicleChoices, normalizeSearchURL, replaceSearchVehicle, searchVehicleLabel, syncSearchInputs } from '@theme/fastutv-search-intent';

// A session preference only: it narrows shopping results and never asserts fitment.
const storageKey = 'fastutv-shopping-vehicle-v1';
const choices = getVehicleChoices();
const nonVehicle = new Set(['racing-fuel', 'utv-apparel-merch-pro-r-turbo-r']);
let selected = null;
let classifiedQuery = null;
const localSearchMode = () => document.documentElement.dataset.fastutvLocalSearch === 'enabled' && !new URL(location.href).searchParams.has('fastutv_algolia_test');
const queryKey = value => String(value || '').trim().replace(/\s+/g, ' ');
const rewrittenAnchors = new Map();
const collectionPath = url => url.pathname.match(/^(.*\/collections\/)([^/]+)(?:\/(.*))?$/);
const vehicle = intent => {
  if(!intent||intent.conflictingMake)return null;
  if(intent.vehicleConstraint?.kind==='explicit-legacy-teryx-seats')return localSearchMode()?{id:'query-legacy-teryx-4',label:searchVehicleLabel(intent)}:null;
  return intent.model?{id:intent.model.id,label:searchVehicleLabel(intent)}:intent.make?{id:intent.make.id,label:intent.make.label}:null;
};
const known = value => {
  const intent = buildSearchIntent(value);
  return !intent.advanced && !intent.conflictingMake && !intent.residual ? vehicle(intent) : null;
};
// Only an explicit merchant-authored platform field supplies a page scope.
// Page titles, product copy and URL fragments never create vehicle fitment.
const trail1000Choices=['Polaris RZR S 1000','Polaris RZR S4 1000','Polaris RZR Trail S 1000'].map(known);
function pagePolicy(url) {
  if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/[^/]+\/?$/i.test(url.pathname)) return null;
  const single=[...document.querySelectorAll('fastutv-vehicle-platform[data-fastutv-shopping-vehicle]')],choice=[...document.querySelectorAll('fastutv-vehicle-platform[data-fastutv-shopping-choice]')],preview=[...document.querySelectorAll('fastutv-vehicle-platform[data-fastutv-shopping-preview]')];
  if(single.length+choice.length+preview.length!==1)return null;
  if(choice.length&&choice[0].getAttribute('data-fastutv-shopping-choice')==='trail-1000')return{mode:'choice',node:choice[0]};
  if(preview.length&&preview[0].getAttribute('data-fastutv-shopping-preview')==='neutral-boost')return{mode:'preview',node:preview[0]};
  const value=single.length?known(single[0].getAttribute('data-fastutv-shopping-vehicle')||''):null;
  return value?{mode:'single',node:single[0],vehicle:value}:null;
}
function pageVehicle(url){return pagePolicy(url)?.vehicle||null;}
function pageChoice(){return trail1000Choices.find(value=>value?.id===selected?.id)||null;}
function syncPageChoices(){
  const current=new URL(location.href),policy=pagePolicy(current);if(policy?.mode!=='choice')return;
  const chosen=pageChoice(),spanish=document.documentElement.lang.startsWith('es'),field=policy.node.querySelector('[data-fastutv-shopping-choice-field]'),status=policy.node.querySelector('[data-fastutv-shopping-choice-status]');
  for(const input of policy.node.querySelectorAll('[data-fastutv-shopping-choice-input]'))input.checked=Boolean(chosen&&known(input.value)?.id===chosen.id);
  if(status)status.textContent=chosen?(spanish?'Modelo elegido: ':'Selected model: ')+chosen.label:selected?(spanish?'El filtro de búsqueda actual es ':'The current search filter is ')+selected.label+(spanish?'. Elige un modelo de esta guía para explorar sus categorías.':'. Choose a model in this guide to browse its categories.'):(spanish?'Elige tu modelo exacto para explorar estas categorías.':'Choose your exact model to browse these categories.');
  for(const anchor of policy.node.querySelectorAll('[data-fastutv-model-choice-link]')){
    const blocked='#'+(field?.id||'');let url;try{url=new URL(anchor.getAttribute('data-fastutv-choice-href')||'',location.href);}catch{anchor.setAttribute('href',blocked);continue;}
    if(!chosen||url.origin!==current.origin||!collectionPath(url)){anchor.setAttribute('href',blocked);continue;}
    url.searchParams.set('fastutv_vehicle',chosen.label);const preview=current.searchParams.get('preview_theme_id');if(preview)url.searchParams.set('preview_theme_id',preview);anchor.setAttribute('href',url.pathname+url.search+url.hash);
  }
}
function saved() {
  try {
    const data = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if(localSearchMode()&&data?.id==='query-legacy-teryx-4'){const constrained=known(data.label);return constrained?.id===data.id?constrained:null;}
    const choice = choices.models.find(item => item.id === data?.id) || choices.makes.find(item => item.id === data?.id);
    const preferred = choice?.makeAliases?.length && typeof data?.label === 'string' ? known(data.label) : null;
    return preferred?.id === choice?.id ? preferred : choice ? known(choice.make ? choice.make + ' ' + choice.label : choice.label) : null;
  } catch { return null; }
}
function remember(value) {
  if (selected?.id !== value?.id) {
    for (const [anchor, originalHref] of rewrittenAnchors) anchor.setAttribute('href', originalHref);
    rewrittenAnchors.clear();
  }
  selected = value;
  try { if (value) sessionStorage.setItem(storageKey, JSON.stringify(value)); else sessionStorage.removeItem(storageKey); } catch { /* Shopping works without storage. */ }
  document.documentElement.dataset.fastutvVehicle = value?.label || '';
  if (localSearchMode()) for (const input of document.querySelectorAll('[data-fastutv-search-form] input[name="fastutv_vehicle"]')) input.value = value?.label || 'all';
  syncLabels();
}
function syncLabels() {
  const current = new URL(location.href);
  const collection = collectionPath(current);
  const root = collection ? collection[1] + 'all' : (document.documentElement.lang.startsWith('es') ? '/es' : '') + '/collections/all';
  for (const node of document.querySelectorAll('[data-fastutv-vehicle-context]')) {
    node.hidden = !selected;
    const label = node.querySelector('[data-fastutv-context-label]'); if (label) label.textContent = selected?.label || '';
    const link = node.querySelector('[data-fastutv-context-link]');
    if (link && selected) { const url = new URL(root, location.origin); url.searchParams.set('fastutv_vehicle', selected.label); const preview = current.searchParams.get('preview_theme_id'); if (preview) url.searchParams.set('preview_theme_id', preview); link.href = url.pathname + url.search; }
  }
  syncPageChoices();
}
function initialize() {
  const url = new URL(location.href), path = collectionPath(url);
  classifiedQuery = null;
  if (localSearchMode() && /\/search\/?$/.test(url.pathname)) {
    // The source has not classified raw q yet. Retain explicit/saved labels
    // without interpreting SKU fragments or injecting a remembered URL scope.
    const explicit = url.searchParams.get('fastutv_vehicle');
    remember(explicit === null ? saved() : explicit === 'all' ? null : known(explicit));
    syncSearchInputs(document, url);
    return;
  }
  const raw = /\/search$/.test(url.pathname) ? buildSearchIntent(displaySearchTerm(url)) : null;
  if (raw?.conflictingMake) { remember(null); return; }
  if (url.searchParams.get('fastutv_vehicle') === 'all' && !vehicle(raw)) { remember(null); return; }
  let current = known(url.searchParams.get('fastutv_vehicle') || '');
  if (vehicle(raw)) current = vehicle(raw);
  else if (url.searchParams.has('fastutv_vehicle') && !current) { remember(null); return; }
  const policy=pagePolicy(url);
  if(!current&&policy?.mode==='choice'){
    const previous=saved(),allowed=trail1000Choices.find(value=>value?.id===previous?.id)||null;remember(allowed);
    // Resolve this entry once. Back must not reinterpret a later session
    // preference as the choice made on an originally neutral guide entry.
    url.searchParams.set('fastutv_vehicle',allowed?.label||'all');history.replaceState(history.state,'',url);return;
  }
  if(!current&&policy?.mode==='preview'){remember(null);return;}
  if (!current) current = pageVehicle(url);
  let config = null;
  try { config = JSON.parse(document.querySelector('[data-discovery-config]')?.textContent || 'null'); } catch { /* Native fallback remains available. */ }
  if (!current && path && !nonVehicle.has(path[2])) {
    const tag = config?.tags?.find(value => /^Browse Model:/i.test(value)) || config?.tags?.find(value => /^Browse Make:/i.test(value));
    const intent = buildSearchIntent(tag ? tag.replace(/^Browse (?:Model|Make):\s*/i, '') : decodeURIComponent(path[2]).replaceAll('-', ' '));
    if (intent.model || intent.make) current = vehicle(intent);
  }
  remember(current || saved());
  if (selected && !raw?.advanced && ((path && !nonVehicle.has(path[2])) || (raw && !current))) {
    url.searchParams.set('fastutv_vehicle', selected.label);
    history.replaceState(history.state, '', url);
  }
}

// Capture before the shared submit normalizer, retaining the shopper's words.
window.addEventListener('submit', event => {
  if (localSearchMode()) return;
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-fastutv-search-form]')) return;
  const input = form.querySelector('input[name="q"]'); if (!input) return;
  const intent = buildSearchIntent(input.value);
  const own = vehicle(intent);
  if (own && !intent.conflictingMake) remember(own);
  if (intent.conflictingMake) remember(null);
  const context = intent.conflictingMake ? null : own || (!intent.advanced ? selected : null);
  let hidden = form.querySelector('input[name="fastutv_vehicle"]');
  if (!hidden) { hidden = document.createElement('input'); hidden.type = 'hidden'; hidden.name = 'fastutv_vehicle'; form.append(hidden); }
  hidden.value = context?.label || 'all';
}, true);
document.addEventListener('fastutv:vehicle-change', event => remember(known(event.detail?.label || '')));
document.addEventListener('change',event=>{
  const input=event.target.closest?.('[data-fastutv-shopping-choice-input]'),policy=pagePolicy(new URL(location.href));
  if(!input?.checked||policy?.mode!=='choice'||input.closest('fastutv-vehicle-platform')!==policy.node)return;
  const value=known(input.value),choice=trail1000Choices.find(item=>item?.id===value?.id);if(!choice)return;
  const url=new URL(location.href);url.searchParams.set('fastutv_vehicle',choice.label);remember(choice);
  document.dispatchEvent(new CustomEvent('fastutv:vehicle-change',{detail:{label:choice.label,url:url.href}}));history.pushState({},'',url);
});
document.addEventListener('fastutv:local-search-context', event => {
  const value = event.detail;
  if (!localSearchMode() || !['sku', 'title', 'words'].includes(value?.queryKind) || queryKey(displaySearchTerm(new URL(location.href))) !== value.query) return;
  classifiedQuery = {query:value.query,kind:value.queryKind};
  remember(known(value.vehicle || '') || (value.queryKind === 'words' ? vehicle(buildSearchIntent(value.query)) : null));
});
document.addEventListener('click', event => {
  const clear = event.target.closest('[data-fastutv-context-clear]');
  if (clear) {
    event.preventDefault(); remember(null);
    let url = new URL(location.href); const path = collectionPath(url);
    if (/\/search\/?$/.test(url.pathname)) {
      if (localSearchMode()) {
        const raw = displaySearchTerm(url), words = classifiedQuery?.query === queryKey(raw) && classifiedQuery.kind === 'words';
        url.searchParams.set('q', words ? replaceSearchVehicle(raw, '') : raw);url.searchParams.delete('fastutv_q');
      } else url = normalizeSearchURL(url, replaceSearchVehicle(displaySearchTerm(url), ''));
    }
    else if (path) {
      const own = buildSearchIntent(decodeURIComponent(path[2]).replaceAll('-', ' '));
      const tags = (path[3] || '').split('+').filter(tag => !/^browse-(?:make|model)-/.test(tag));
      url.pathname = path[1] + (own.model || own.make ? 'all' : path[2]) + (tags.length ? '/' + tags.join('+') : '');
    } else if (pagePolicy(url)) {
      url.searchParams.set('fastutv_vehicle', 'all');
      document.dispatchEvent(new CustomEvent('fastutv:vehicle-change', {detail:{label:'',url:url.href}}));
      history.pushState({}, '', url);
      // Keep the shopper's unsubmitted header words on a non-search page.
      return;
    } else return;
    url.searchParams.set('fastutv_vehicle', 'all'); url.searchParams.delete('page'); url.searchParams.delete('fastutv_page');
    if (localSearchMode() && /\/search\/?$/.test(url.pathname) && document.querySelector('fastutv-discovery[data-kind="search"]')) {
      document.dispatchEvent(new CustomEvent('fastutv:vehicle-change', {detail:{label:'',url:url.href}}));
      history.pushState({}, '', url);syncSearchInputs(document, url);
      document.dispatchEvent(new CustomEvent('fastutv:local-search-navigation', {detail:{url:url.href}}));return;
    }
    location.assign(url.href); return;
  }
  const anchor = event.target.closest('a[href]');
  const policy=pagePolicy(new URL(location.href));
  if(anchor?.hasAttribute('data-fastutv-model-choice-link')&&policy?.mode==='choice'&&anchor.closest('fastutv-vehicle-platform')===policy.node&&!pageChoice()){
    event.preventDefault();policy.node.querySelector('[data-fastutv-shopping-choice-field]')?.focus();return;
  }
  if (!anchor || !selected || anchor.hasAttribute('download')) return;
  const originalHref = rewrittenAnchors.get(anchor) ?? anchor.getAttribute('href');
  const url = new URL(originalHref, location.href), path = collectionPath(url);
  if (url.origin !== location.origin || !path || nonVehicle.has(path[2]) || url.searchParams.has('fastutv_vehicle')) return;
  const target = buildSearchIntent(decodeURIComponent(path[2] + ' ' + (path[3] || '')).replaceAll('-', ' '));
  if (target.make || target.model) return;
  rewrittenAnchors.set(anchor, originalHref);
  url.searchParams.set('fastutv_vehicle', selected.label); anchor.href = url.href;
}, true);
initialize();
window.addEventListener('popstate', initialize);
window.addEventListener('pageshow', event => event.persisted ? initialize() : syncLabels());
document.addEventListener('shopify:section:load', syncLabels);
